import type { ChatType } from '@kanbu/schema';
import {
  createChatSchema,
  getDefaultBuiltinToolAllowlistForAgent,
  updateChatSchema,
  type CreateChatDTO,
  type UpdateChatDTO,
} from '@kanbu/schema/contracts';
import { Agent, AgentType, ChatModel, Role } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { Button, toast } from '@utima/ui';
import { useFieldState, useFormApi } from 'informed';
import { Copy, File, FileScan } from 'lucide-react';
import { memo, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';

import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { MetaFields } from '@/components/fields/MetaFields';
import { createLazyFileUploadManager } from '@/components/fileUpload/adapters/lazyFileUploadAdapter';
import { EntityCRUDForm } from '@/components/form/EntityCRUDForm';
import { Protected } from '@/components/protected/Protected';
import { VoiceButton, VoiceProvider } from '@/components/voice';
import { useBoundStore } from '@/store/store';

import { AdvancedConfigFields } from './formFields/AdvancedConfigFields';
import { BasicConfigFields } from './formFields/BasicConfigFields';
import { ChatbotScriptFields } from './formFields/ChatbotScriptFields';
import { ChatPlacementFields } from './formFields/ChatPlacementFields';
import { ChatThemeFields } from './formFields/ChatThemeFields';
import { DomainPolicyFields } from './formFields/DomainPolicyFields';
import { EvaluationFields } from './formFields/EvaluationFields';
import { OverridesFields } from './formFields/OverridesFields';
import { PersonalizationFields } from './formFields/PersonalizationFields';
import { SecurityFields } from './formFields/SecurityFields';
import { SuperAdminFields } from './formFields/SuperAdminFields';
import { TwilioConfigFields } from './formFields/TwilioConfigFields';
import {
  VoiceCallHandlingFields,
  VoiceConfigFields,
} from './formFields/VoiceConfigFields';
import { useBuiltinToolAllowlistSync } from './useBuiltinToolAllowlistSync';
import { useChatMutation } from '../useChatMutation';

function ChatFormSyncEffects() {
  useBuiltinToolAllowlistSync();

  return null;
}

function ChatTestCall({
  chatId,
  target,
  onSave,
}: {
  chatId: string;
  target: HTMLElement;
  onSave: (values: UpdateChatDTO) => Promise<unknown>;
}) {
  const { t } = useLingui();
  const agentType = useFieldState('agentType').value;
  const formApi = useFormApi();

  if (agentType !== AgentType.Voice) return null;

  return createPortal(
    <VoiceProvider
      chatId={chatId}
      onError={error =>
        toast.error(t`Voice call error`, { description: error.message })
      }
    >
      <VoiceButton
        beforeConnect={async () => {
          formApi.validate();
          if (Object.keys(formApi.getFormState().errors).length)
            throw new Error('Invalid configuration');
          await onSave(
            updateChatSchema.parse({
              ...formApi.getFormState().values,
              id: chatId,
            }),
          );
        }}
      />
    </VoiceProvider>,
    target,
  );
}

export type ChatFormProps = {
  data?: ChatType;
  actionsTarget?: HTMLElement | null;
};

export const ChatForm = memo(function ChatForm({
  data,
  actionsTarget,
}: ChatFormProps) {
  const { t } = useLingui();
  const { setChat } = useBoundStore(
    useShallow(state => ({
      setChat: state.setChat,
    })),
  );

  const fileUploadManager = useRef(createLazyFileUploadManager());
  const uploadAdapters = useMemo(
    () => ({
      avatar: fileUploadManager.current.getAdapter('avatar'),
      logo: fileUploadManager.current.getAdapter('logo'),
      bubble: fileUploadManager.current.getAdapter('bubble'),
    }),
    [],
  );

  const {
    create: { mutateAsync: create },
    remove: { mutateAsync: remove },
    update: { mutateAsync: update },
    duplicate: { mutateAsync: duplicate, isPending: isDuplicating },
  } = useChatMutation();
  const navigate = useNavigate();

  const handleDuplicate = async (id: string) => {
    try {
      const copy = await duplicate({ id });

      setChat(copy);

      await navigate({
        to: '/chats/$chatId/edit',
        params: { chatId: copy.id },
      });
    } catch {
      toast.error(t`Failed to duplicate.`);
    }
  };

  const formInitialData = useMemo(() => {
    if (!data) {
      const agent = Agent.Conversational;

      /**
       * Create-mode seed defaults — no persisted entity yet, so there is no
       * `id`; cast to keep the memo's type uniform (`ChatType`) for the form.
       */
      return {
        agent,
        agentType: AgentType.Chat,
        model: ChatModel.Gpt6Luna,
        smallModel: ChatModel.Gemini25FlashLite,
        agentConfig: {
          enabledBuiltinTools: getDefaultBuiltinToolAllowlistForAgent(agent),
        },
      } as ChatType;
    }

    const agent = data.agent ?? Agent.Conversational;

    if (agent !== Agent.Conversational && agent !== Agent.ShopAssistant) {
      return data;
    }

    const raw = data.agentConfig?.enabledBuiltinTools;

    if (Array.isArray(raw)) {
      return data;
    }

    return {
      ...data,
      agentConfig: {
        ...data.agentConfig,
        enabledBuiltinTools: getDefaultBuiltinToolAllowlistForAgent(agent),
      },
    };
  }, [data]);

  /**
   * Commit (upload/delete) images (avatar, bubbleLogo and logo).
   *
   * It also returns `values` objects containing PublicUrl of uploaded images
   * in it's respective place, so for example, for avatar it updates `values.chatbotConfig.systemAvatar`
   */
  const commitFileValues = useCallback(
    async <T extends CreateChatDTO | UpdateChatDTO>(values: T): Promise<T> => {
      const results = await fileUploadManager.current.commitAll();

      // Use the last result from each adapter's queue (handles delete -> upload scenarios)
      const avatar = results.avatar?.at(-1);
      const logo = results.logo?.at(-1);
      const bubble = results.bubble?.at(-1);

      if (avatar) {
        values.chatbotConfig = {
          ...values?.chatbotConfig,
          systemAvatar: avatar.publicUrl ?? undefined,
        };
      }

      if (logo) {
        values.themeConfig = {
          ...values?.themeConfig,
          logo: logo.publicUrl ?? undefined,
        };
      }

      if (bubble) {
        values.themeConfig = {
          ...values?.themeConfig,
          logoBubble: bubble.publicUrl ?? undefined,
        };
      }

      return values;
    },
    [],
  );

  const saveConfiguration = async (values: UpdateChatDTO) => {
    const fileValues = await commitFileValues(values);

    return update(fileValues);
  };

  return (
    <EntityCRUDForm
      /**
       * We need to pass key to ChatForm to force re-render when chatId changes,
       * this is to properly reset initial form data when chatId changes.
       */
      key={data?.id}
      keepState
      initialData={formInitialData}
      createSchema={createChatSchema}
      updateSchema={updateChatSchema}
      onCreate={async values => {
        const fileValues = await commitFileValues(values);
        const newChat = await create(fileValues);

        // Set new chat as active
        setChat(newChat);

        // Return new chat
        return newChat;
      }}
      onUpdate={saveConfiguration}
      onRemove={async ({ id }) => {
        await remove({ id });

        if (useBoundStore.getState().chat?.id === id) {
          setChat(null);
        }
      }}
      customRedirect='/inbox'
      basePath='/chats'
      idParamName='chatId'
      extraActions={
        data?.id && (
          <Button
            data-cta='admin-chats-edit-duplicate-button'
            type='button'
            variant='secondary'
            icon={<Copy size={18} />}
            loading={isDuplicating}
            disabled={isDuplicating}
            onClick={() => handleDuplicate(data.id)}
          >
            <span className='hidden sm:inline'>{t`Duplicate agent`}</span>
          </Button>
        )
      }
    >
      <ChatFormSyncEffects />
      {data?.id && actionsTarget && (
        <ChatTestCall
          chatId={data.id}
          target={actionsTarget}
          onSave={saveConfiguration}
        />
      )}
      <EntityCRUDForm.Col>
        <BasicConfigFields />
        <SecurityFields />
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <TwilioConfigFields data={data} />
        <PersonalizationFields />
        <ChatThemeFields
          logoUploadAdapter={uploadAdapters.logo}
          avatarUploadAdapter={uploadAdapters.avatar}
          bubbleUploadAdapter={uploadAdapters.bubble}
          data={data}
        />
        <ChatPlacementFields />
        <VoiceConfigFields />
        <VoiceCallHandlingFields />
      </EntityCRUDForm.Col>
      <EntityCRUDForm.Col>
        <MetaFields data={data}>
          <Protected roles={[Role.SuperAdmin]}>
            <DisplayValue label={t`Documents count`} Icon={File}>
              {data?.documentsCount ?? '0'}
            </DisplayValue>
            <DisplayValue label={t`Embeddings count`} Icon={FileScan}>
              {data?.embeddingsCount ?? '0'}
            </DisplayValue>
          </Protected>
        </MetaFields>
        <DomainPolicyFields />
        <ChatbotScriptFields data={data} />
        <AdvancedConfigFields />
        <Protected roles={[Role.SuperAdmin]}>
          <EvaluationFields data={data} />
          <SuperAdminFields />
          <OverridesFields />
        </Protected>
      </EntityCRUDForm.Col>
    </EntityCRUDForm>
  );
});
